import { useExerciseIdsSelect, useTempExercises } from '@entities/exercise-video/model.ts';
import cn from 'classnames';
import { useCallback, useLayoutEffect, useState, type MutableRefObject } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useExercisePageRedirect } from '@pages/programs/exercise/helpers.ts';
import {
  ActionWrapper,
  ExerciseCard,
  ExerciseCardMobile,
  ExerciseDuration,
} from '@features/exercises';
import {
  AddPromoCard,
  DnDCard,
  DnDList,
  Periodicity,
  type ProgramEditorValues,
} from '@features/programs';
import { AddVideoMobile, PeriodicityMobile, Schedule } from '@features/programs/editor-form';
import { Close, LibraryPlayButton } from '@shared/assets';
import { areArraysWithNumbersEqualStrict, doCheckArray } from '@shared/helpers/index.ts';
import { useMobile } from '@shared/hooks';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';
import { IconButton } from '@shared/ui';
import * as st from './styles.module.scss';
import { AddExercise } from './ui/add-exercise';

interface Props {
  editorFormData: any;
  onRedirect?: () => void;
  initVideosIds: number[];
  onVideosIdsChange: (ids: number[]) => void;
  moduleRef?: MutableRefObject<HTMLElement | null>;
}

export const EditorVideos = ({
  editorFormData,
  onRedirect,
  initVideosIds,
  onVideosIdsChange,
  moduleRef,
}: Props): JSX.Element => {
  // const navigate = useNavigate();
  const { doRedirectWithSavePageUrl: goToExercisePage } = useExercisePageRedirect();
  const { code } = useParams();
  const { doRedirectWithSavePageUrl } = usePagesRedirect('librarySelection');
  // const url = useResolvedPath('').pathname;
  const { getValues } = useFormContext<ProgramEditorValues>();
  const { isMobile } = useMobile();
  const { methods, removeExercise } = editorFormData;
  const [startExerciseAdd, setStartExerciseAdd] = useState(false);

  const {
    selectedIds: exercises,
    doResetSelectedIds: resetSelectedExerciseIds,
    setIsSelectingTrue,
  } = useExerciseIdsSelect();

  const { watch, setError } = methods;

  const exercisesInForm = watch('exercises');
  // console.log('exercises: ', exercises);

  useLayoutEffect(() => {
    doCheckArray(initVideosIds) && resetSelectedExerciseIds(initVideosIds);
  }, []);

  // console.log('exercises:', exercises);

  useLayoutEffect(() => {
    exercises &&
      !areArraysWithNumbersEqualStrict({ firstArray: exercisesInForm, secondArray: exercises }) &&
      onVideosIdsChange &&
      onVideosIdsChange(exercises);
  }, [exercises, exercisesInForm, onVideosIdsChange]);

  const periodicity = watch('periodicity');
  const schedule = watch('schedule');
  const module = getValues('module');
  const { tempExerciseIds } = useTempExercises();
  const saveStateAndNavigate = (newUrl: string) => {
    onRedirect && onRedirect();
    goToExercisePage(newUrl, { state: { wasSelecting: true } });
  };

  const onVideoAdd = useCallback(() => {
    if (module === 0) {
      setError('module', { type: 'custom', message: 'Выберите модуль' });
      moduleRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      onRedirect && onRedirect();
      setStartExerciseAdd(true);
    }
  }, [module, moduleRef, onRedirect, setError]);

  const selectExerciseFromLibrary = () => {
    // const search = JSON.stringify(exercises);
    // saveStateAndNavigate(`select-exercises`);
    setIsSelectingTrue();
    onRedirect && onRedirect();
    doRedirectWithSavePageUrl(`/programs/${code}/exercises`);
    // saveStateAndNavigate(`/programs/${code}/exercises`);
  };

  const goToExercise = (id: number, isTempExercise: boolean): void => {
    // если это не временное видео, при нажатии, перемещаемся на видео из библиотеки. А если временное - на временное видео
    saveStateAndNavigate(`${isTempExercise ? '' : '/programs/library/'}exercise/${id}`);
  };

  return (
    <fieldset className={st.videos} id={'formVideos'}>
      <section className={st.nav}>
        <div className={st.nav__tabs}>
          <h3 className={st.title}>Видео</h3>
        </div>

        <AddExercise
          startExerciseAdd={startExerciseAdd}
          setStartExerciseAdd={setStartExerciseAdd}
          onAddFromLibrary={selectExerciseFromLibrary}
        />
      </section>
      <DnDList className={exercises.length === 0 && isMobile ? st.emptyList : undefined}>
        {exercises.map((id: number, index: number) => {
          return (
            <DnDCard key={id} index={index} saveFormData={onRedirect}>
              {isMobile ? (
                <ExerciseCardMobile
                  className={st.exercise}
                  videoId={id}
                  RightBtn={
                    <IconButton
                      variant="dark"
                      size="small"
                      type="button"
                      onClick={() => removeExercise(id)}
                    >
                      <div className={st.close}>
                        <Close />
                      </div>
                    </IconButton>
                  }
                  onExerciseClick={() => goToExercise(id, tempExerciseIds.includes(id))}
                  Periodicity={
                    periodicity && (
                      <div className={cn(st.periodicity, schedule ? st.withSchedule : undefined)}>
                        <PeriodicityMobile id={id} />
                      </div>
                    )
                  }
                  Schecule={schedule ? <Schedule index={index} id={id} /> : undefined}
                />
              ) : (
                <ExerciseCard
                  videoId={id}
                  // footer={
                  //   periodicity ? (
                  //     <div className={st.periodicity}>
                  //       <PeriodicityMobile id={id} />
                  //     </div>
                  //   ) : (
                  //     <></>
                  //   )
                  // }
                >
                  <ActionWrapper
                    alwaysVisible={periodicity || schedule}
                    variant="transparent"
                    withTransition={false}
                    Duration={<ExerciseDuration videoId={id} />}
                    Menu={
                      <IconButton
                        variant="white"
                        size={'small'}
                        type="button"
                        onClick={() => removeExercise(id)}
                      >
                        <div className={st.close}>
                          <Close />
                        </div>
                      </IconButton>
                    }
                    Periodicity={periodicity ? <Periodicity id={id} /> : <></>}
                    Schedule={schedule ? <Schedule index={index} id={id} /> : undefined}
                    Play={
                      <IconButton
                        variant="white"
                        type="button"
                        onClick={() => goToExercise(id, tempExerciseIds.includes(id))}
                      >
                        <LibraryPlayButton className={st.playButton} />
                      </IconButton>
                      // <IconButton
                      //   variant="white"
                      //   type="button"
                      //   size={'medium'}
                      //   onClick={() => goToExercise(id, tempExerciseIds.includes(id))}
                      // >
                      //   <div className={st.play}>
                      //     <ArrowRightBig />
                      //   </div>
                      // </IconButton>
                    }
                  />
                </ExerciseCard>
              )}
            </DnDCard>
          );
        })}

        {!isMobile ? <AddPromoCard onClick={onVideoAdd} /> : null}
      </DnDList>
      <div className={st.addMobile} onClick={onVideoAdd}>
        <AddVideoMobile />
      </div>
    </fieldset>
  );
};
